import { useRef } from 'react';
import { motion, useReducedMotion, useScroll, useTransform } from 'framer-motion';
import { NAV_ITEMS, LOGO, INSTAGRAM_URL, INSTAGRAM_HANDLE, WHATSAPP_DISPLAY, LOCATION, whatsappLink, DEFAULT_MESSAGE } from '@/data/site';
import { useExperience } from '@/lib/experience';
import { Wordmark } from './brand/Wordmark';
import { WhatsAppIcon, InstagramIcon, Heart } from './brand/Shapes';
import { Wave } from './fx/Wave';

const BG = '#EAF4FF';
const SERVICES = ['Banho Porte Pequeno', 'Banho Porte Médio', 'Banho Porte Grande', 'Pacotes Mensais', 'Passeios'];

export const Footer = () => {
  const { scrollTo } = useExperience();
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end end'] });
  const wordY = useTransform(scrollYProgress, [0, 1], ['60%', '0%']);
  const year = new Date().getFullYear();

  return (
    <footer ref={ref} data-testid="footer" className="relative text-pet-ink mt-16 md:mt-24" style={{ background: BG }}>
      <Wave color={BG} className="absolute bottom-[calc(100%-1px)] inset-x-0" />
      <div className="container-cp pt-10 md:pt-16">
        <div className="grid grid-cols-2 md:grid-cols-12 gap-10">
          <div className="col-span-2 md:col-span-5">
            <div className="flex items-center gap-3">
              <img src={LOGO} alt="" className="w-14 h-14 rounded-full ring-[3px] ring-pet-pink" />
              <Wordmark className="text-3xl text-pet-blue" withPet />
            </div>
            <p className="mt-5 font-bold text-pet-ink/70 leading-relaxed max-w-sm">
              Cuidamos do seu pet com todo o amor e carinho que ele merece. Banho, tosa e muito carinho!
            </p>
            <div className="mt-6 flex items-center gap-3">
              <motion.a
                href={whatsappLink(DEFAULT_MESSAGE)}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="WhatsApp"
                whileHover={{ scale: 1.12, rotate: -8 }}
                whileTap={{ scale: 0.9 }}
                className="w-12 h-12 rounded-full bg-pet-whats text-white flex items-center justify-center shadow-md"
              >
                <WhatsAppIcon className="w-6 h-6" />
              </motion.a>
              <motion.a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                whileHover={{ scale: 1.12, rotate: 8 }}
                whileTap={{ scale: 0.9 }}
                className="w-12 h-12 rounded-full bg-gradient-to-br from-[#E1306C] to-[#F77737] text-white flex items-center justify-center shadow-md"
              >
                <InstagramIcon className="w-5 h-5" />
              </motion.a>
            </div>
          </div>

          <nav aria-label="Rodapé" className="md:col-span-2">
            <p className="font-display text-lg text-pet-blue mb-4">Navegue</p>
            <ul className="space-y-2.5">
              {NAV_ITEMS.map((item) => (
                <li key={item.id}>
                  <button type="button" onClick={() => scrollTo(item.id === 'inicio' ? 'top' : item.id)} className="roll-parent font-bold text-pet-ink/80 hover:text-pet-blue">
                    <span className="roll">
                      <span>{item.label}</span>
                      <span aria-hidden="true">{item.label}</span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </nav>

          <div className="md:col-span-3">
            <p className="font-display text-lg text-pet-blue mb-4">Serviços</p>
            <ul className="space-y-2.5 font-bold text-pet-ink/80">
              {SERVICES.map((s) => (
                <li key={s}>{s}</li>
              ))}
            </ul>
          </div>

          <div className="col-span-2 md:col-span-2">
            <p className="font-display text-lg text-pet-blue mb-4">Contato</p>
            <ul className="space-y-2.5 font-bold text-pet-ink/80">
              <li>{WHATSAPP_DISPLAY}</li>
              <li>{LOCATION}</li>
              <li className="break-words">
                @caopanhia_pet<wbr />.oficial
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-14 md:mt-16 flex flex-col-reverse md:flex-row md:items-center justify-between gap-4 border-t-2 border-dashed border-pet-blue/20 pt-6 text-sm font-bold text-pet-ink/60">
          <p>© {year} Cãopanhia Pet. Todos os direitos reservados.</p>
          <p className="flex items-center gap-2">
            Feito com <Heart className="w-4 h-4 text-pet-pink-deep heartbeat" /> para pets e seus donos
          </p>
          <button type="button" onClick={() => scrollTo('top')} className="text-left md:text-right text-pet-blue hover:text-pet-pink-deep">
            Voltar ao topo ↑
          </button>
        </div>
      </div>

      <div className="relative mt-6 md:mt-10 overflow-hidden" aria-hidden="true">
        <motion.div style={{ y: reduce ? 0 : wordY }} className="flex justify-center text-pet-blue">
          <Wordmark className="text-[19vw] tracking-[-0.03em] leading-[0.85] pb-[2vw]" />
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;
